import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import { Button, Card, Container } from 'react-bootstrap';
import { DataGrid, GridToolbar,GridToolbarExport } from "@mui/x-data-grid";
import axios from 'axios';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TableHead } from '@mui/material';
import { AppBar, Toolbar,Alert } from '@mui/material';
import PropTypes from 'prop-types';
import { apiPath } from "../apiurl.js";
import LoadingSequence from "../assets/Loading/LoadingSequence";
import { useHistory, useLocation } from 'react-router-dom';
import SequenceAlignment from "views/SequenceAlignment.js";
import exportPDF from '../utils/exportPDF.js';

const ReadHits = ({ isDashboard = false }) => {
  ReadHits.propTypes = {
    isDashboard: PropTypes.bool,
  };

  const history = useHistory();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const run = new URLSearchParams(location.search).get('run');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true); // Track loading state
  const [error, setError] = useState(null); // Track error state
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalContent, setModalContent] = useState('');
  const [openQseqSseqModal, setOpenQseqSseqModal] = useState(false);
  const [qseqSseqContent, setQseqSseqContent] = useState('');
  const filters = JSON.parse(new URLSearchParams(location.search).get('filters') || '{}');
  const ROW_LIMIT = 500000; // Define row limit
  const [infoMessage, setInfoMessage] = useState(""); // State to store the alert message
  const [totalRows, setTotalRows] = useState(0); // Store total row count

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiPath}/api/readhit/${run}`);
        const total = response.data.length;
        setTotalRows(total);
  
        let displayedData = response.data;
        if (total > ROW_LIMIT) {
          displayedData = response.data.slice(0, ROW_LIMIT);
          setInfoMessage(`Due to the file size for SRA Run: ${run} file, we only show the first ${ROW_LIMIT.toLocaleString()} rows. The total number of rows is ${total.toLocaleString()}.`);
        }
  
        // ✅ Add a UNIQUE ID to each row
        const formattedData = displayedData.map((item, index) => ({
          ...item,
          id: `${item.qseqid}_${item.sseqid}_${index}` // Unique combination
        }));
  
        setData(formattedData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [run]);
  
  
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleBackClick = () => {
    const filterModel = {
      items: [
        { field: 'Run', operator: 'contains', id: Math.random(), value: run, fromInput: '' }
      ]
    };
    const filterModelParam = encodeURIComponent(JSON.stringify(filterModel));
    history.push(`/pass/datatable?filters=${filterModelParam}`);
  };

  const handleCloseQseqSseqModal = () => {
    setOpenQseqSseqModal(false);
  };

  const handleOpenQseqSseqModal = () => {
    setOpenQseqSseqModal(true);
  };

  const handleQseqSseqClick = (params) => {
    setSelectedRow(params.row);
    setQseqSseqContent(params.row.qseq || params.row.sseq);
    handleOpenQseqSseqModal();
  };

  const columns = [
    { field: 'qseqid', headerName: 'qseqid', width: 135 },
    { field: 'sseqid', headerName: 'sseqid', width: 135 },
    { field: 'pident', headerName: 'pident', width: 135 },
    { field: 'qlen', headerName: 'qlen', width: 135 },
    { field: 'len', headerName: 'len', width: 135 },
    { field: 'mm', headerName: 'mm', width: 135 },
    { field: 'gap', headerName: 'gap', width: 135 },
    { field: 'qstart', headerName: 'qstart', width: 135 },
    { field: 'qend', headerName: 'qend', width: 135 },
    { field: 'sstart', headerName: 'sstart', width: 135 },
    { field: 'send', headerName: 'send', width: 135 },
    { field: 'evalue', headerName: 'evalue', width: 135 },
    { field: 'bit', headerName: 'bit', width: 135 },
    { field: 'poltype', headerName: 'poltype', width: 135 },
    { field: 'polstart', headerName: 'polstart', width: 135 },
    { field: 'polend', headerName: 'polend', width: 135 },
    { field: 'qseq', headerName: 'qseq', width: 135 },
    { field: 'sseq', headerName: 'sseq', width: 135 },
    {
      field: 'sequencevisua',
      headerName: 'Sequence Visual',
      width: 135,
      renderCell: (params) => (
        <Button
          variant="outlined"
          style={{ backgroundColor: colors.blueAccent[900], color: 'white' }}
          onClick={() => handleQseqSseqClick(params)}
        >
          Preview
        </Button>
      ),
    },
    {
      field: 'preview',
      headerName: 'Preview',
      width: 120,
      renderCell: (params) => (
        <Button
          variant="outlined"
          style={{ backgroundColor: colors.blueAccent[900], color: 'white' }}
          onClick={() => {
            setSelectedRow(params.row);
            setOpenModal(true);
          }}
        >
          Preview
        </Button>
      ),
    },
  ];

  const handleDownloadJSON = () => {
    if (selectedRow && selectedRow.Run) {
      const runValue = selectedRow.Run;
      const jsonContent = JSON.stringify(selectedRow, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `row_details_${runValue}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (loading) {
    return <LoadingSequence message="Loading data" size="large" fullPage />;
  }

  if (error) {
    return <Typography variant="h6" align="center" color="error">{error}</Typography>;
  }
  const CustomNoRowsOverlay = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Typography variant="subtitle1">No data yet</Typography>
    </Box>
  );
  const CustomToolbar = () => (
    <GridToolbar>
      <GridToolbarExport csvOptions={csvOptions} />
    </GridToolbar>
  );
  return (
    <>
      <Container fluid>
        <Card id="contentToExport">
          <Card.Header>
          {infoMessage && (
                    <Alert variant="filled" severity="info" sx={{ mb: 2 }}>
                      {infoMessage}
                    </Alert>
                  )}
            <Container>
              <AppBar position="static" sx={{ backgroundColor: 'white', textAlign: 'center' }}>
                <Toolbar>
                  <Button onClick={handleBackClick} type="button" className="btn btn-outline-primary btn-sm">
                    Back to Datatable
                  </Button>
                  <Typography variant="h6" component="div" sx={{ color: 'black', flexGrow: 1, fontSize: '24px' }}>
                    Run: {run}
                  </Typography>
                </Toolbar>
              </AppBar>
            </Container>
          </Card.Header>
          <Card.Body style={{ height: "75Vh" }}>
          <DataGrid
              rows={data}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 20]}
              checkboxSelection
              slots={{ toolbar: GridToolbar }} 
              getRowId={(row) => row.id}
            />

            {/* Preview Modal */}
            <Modal
              open={openModal}
              onClose={handleCloseModal}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: "70%", height: "75%", bgcolor: 'background.paper', boxShadow: 24, p: 4 }}>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                  Row Details
                </Typography>
                {selectedRow && (
                  <div style={{ maxHeight: '600px', overflow: 'auto', marginTop: '10px' }}>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableBody>
                          {Object.entries(selectedRow).map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell component="th" scope="row">{key}</TableCell>
                              <TableCell>
                                {key === 'Tasks' ? (
                                  <TableContainer component={Paper}>
                                    <Table size="small" aria-label="tasks">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Task</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell>Seconds</TableCell>
                                          <TableCell>Bytes</TableCell>
                                          <TableCell>GB</TableCell>
                                          <TableCell>Command</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {Object.entries(value).map(([taskName, taskDetails]) => (
                                          <TableRow key={taskName}>
                                            <TableCell>{taskName}</TableCell>
                                            <TableCell>{taskDetails.status}</TableCell>
                                            <TableCell>{taskDetails.seconds}</TableCell>
                                            <TableCell>{taskDetails.bytes}</TableCell>
                                            <TableCell>{taskDetails.gb}</TableCell>
                                            <TableCell>{taskDetails.command}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                ) : (
                                  key === 'qseq' || key === 'sseq' ? (
                                    <div>
                                      <Typography variant="subtitle1">{key} Content:</Typography>
                                      <pre>{modalContent}</pre>
                                    </div>
                                  ) : (
                                    typeof value === 'object' ? JSON.stringify(value) : value
                                  )
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                )}
                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
                  <Button onClick={handleDownloadJSON} variant="outlined" color="primary">
                    Download JSON
                  </Button>
                  <Button onClick={handleCloseModal} color="primary" size="large" variant="contained">
                    Close
                  </Button>
                </div>
              </Box>
            </Modal>

            {/* Combined Qseq/Sseq Modal */}
            <Modal
              open={openQseqSseqModal}
              onClose={handleCloseQseqSseqModal}
              aria-labelledby="modal-qseqsseq-title"
              aria-describedby="modal-qseqsseq-description"
            >
              <Box id="modalContentToExport" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: "70%", height: "80%", bgcolor: 'background.paper', boxShadow: 24, p: 4 }}>
                <Typography>
                  Sequence Alignment Visualization QSED & SSEQ
                </Typography>
                {selectedRow && (
                  <div className="Modaltable" style={{ maxHeight: '69Vh', overflow: 'auto', marginTop: '-10px' }}>
                    <Box>
                      {selectedRow && (
                        <SequenceAlignment alignmentData={selectedRow} />
                      )}
                    </Box>
                  </div>
                )}
                <div style={{ marginTop: '10px', textAlign: 'right' }}>
                  <Button variant="primary" onClick={() => exportPDF('modalContentToExport')}>Export as PDF</Button>
                  <Button onClick={handleCloseQseqSseqModal} color="primary" size="large" variant="contained">
                    Close
                  </Button>
                </div>
              </Box>
            </Modal>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ReadHits;